// src/Center.tsx
import { useCallback } from "react";
import type { CSSProperties } from "react";
import ReactECharts from "echarts-for-react";

const ACCENT = "#00B8BA";

type SlideId =
  | "sl2"
  | "sl3"
  | "sl4"
  | "sl5"
  | "sl6"
  | "sl7"
  | "sl8"
  | "sl9"
  | "sl10"
  | "sl11"
  | "sl12"
  | "sl13"
  | "sl14"
  | "sl15";

const pageCss: CSSProperties = {
  fontFamily: "Inter, system-ui, Arial, sans-serif",
  background: "#f7f8fa",
  minHeight: "100vh",
  color: "#0f172a",
  margin: 0,
};

const containerCss: CSSProperties = {
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "16px 24px 32px",
  boxSizing: "border-box",
};

const headerCss: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginBottom: 16,
};

const titleCss: CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
};

const subtitleCss: CSSProperties = {
  fontSize: 14,
  color: "#6b7280",
};

const filterBarCss: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  marginTop: 8,
};

const filterInputCss: CSSProperties = {
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  fontSize: 13,
  background: "#fff",
};

const sectionListCss: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  marginTop: 16,
};

const cardCss: CSSProperties = {
  background: "#ffffff",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  padding: 12,
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
};

const cardTitleCss: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  marginBottom: 4,
};

const cardSubCss: CSSProperties = {
  fontSize: 12,
  color: "#6b7280",
  marginBottom: 8,
};

const chartContainerCss: CSSProperties = {
  width: "100%",
  height: 260,
};

const tallChartCss: CSSProperties = {
  width: "100%",
  height: 420,
};

/* ========= Генераторы опций ECharts (статичные демо-данные) ========= */

// Сл2: Количество услуг по ГОБМП (по месяцам 2024)
function chartServicesGOBMP(): any {
  const months = [
    "Янв",
    "Фев",
    "Мар",
    "Апр",
    "Май",
    "Июн",
    "Июл",
    "Авг",
    "Сен",
    "Окт",
    "Ноя",
    "Дек",
  ];
  const values = [
    1200, 1300, 1250, 1400, 1500, 1600, 1700, 1680, 1750, 1820, 1900, 2100,
  ];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 40, right: 20, top: 30, bottom: 40 },
    xAxis: { type: "category", data: months },
    yAxis: { type: "value", name: "кол-во услуг" },
    series: [{ name: "ГОБМП", type: "bar", data: values }],
    color: [ACCENT],
  };
}

// Сл3: Количество услуг по ОСМС (по месяцам 2024)
function chartServicesOSMS(): any {
  const months = [
    "Янв",
    "Фев",
    "Мар",
    "Апр",
    "Май",
    "Июн",
    "Июл",
    "Авг",
    "Сен",
    "Окт",
    "Ноя",
    "Дек",
  ];
  const values = [800, 900, 880, 950, 1000, 1100, 1150, 1170, 1230, 1300, 1340, 1400];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 40, right: 20, top: 30, bottom: 40 },
    xAxis: { type: "category", data: months },
    yAxis: { type: "value", name: "кол-во услуг" },
    series: [{ name: "ОСМС", type: "bar", data: values }],
    color: ["#6366f1"],
  };
}

// Сл4: ГОБМП и ОСМС по годам
function chartGOBMP_OSMS_ByYears(): any {
  const years = ["2021", "2022", "2023", "2024"];
  const gobmp = [12000, 13500, 15000, 16500];
  const osms = [9000, 10500, 11800, 13000];
  return {
    tooltip: { trigger: "axis" },
    legend: { data: ["ГОБМП", "ОСМС"] },
    grid: { left: 40, right: 20, top: 40, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "кол-во услуг" },
    series: [
      { name: "ГОБМП", type: "bar", stack: "total", data: gobmp },
      { name: "ОСМС", type: "bar", stack: "total", data: osms },
    ],
    color: [ACCENT, "#6366f1"],
  };
}

// Сл5: ГОБМП в 2024 по месяцам
function chartGOBMP2024_ByMonth(): any {
  const months = [
    "Янв",
    "Фев",
    "Мар",
    "Апр",
    "Май",
    "Июн",
    "Июл",
    "Авг",
    "Сен",
    "Окт",
    "Ноя",
    "Дек",
  ];
  const values = [
    1200, 1300, 1450, 1600, 1700, 1800, 1900, 2000, 2100, 2200, 2300, 2500,
  ];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 40, right: 20, top: 30, bottom: 40 },
    xAxis: { type: "category", data: months },
    yAxis: { type: "value", name: "кол-во услуг" },
    series: [{ name: "ГОБМП 2024", type: "line", smooth: true, data: values }],
    color: [ACCENT],
  };
}

// Сл6: ГОБМП в феврале 2024 по дням
function chartGOBMP_Feb2024_ByDay(): any {
  const days = Array.from({ length: 29 }, (_, i) => String(i + 1));
  const values = days.map(
    (_, i) => 30 + Math.round(20 * Math.sin(i / 3)) + (i % 5) * 2
  );
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 40, right: 20, top: 30, bottom: 40 },
    xAxis: { type: "category", data: days },
    yAxis: { type: "value", name: "кол-во услуг" },
    series: [{ name: "Февраль 2024, ГОБМП", type: "bar", data: values }],
    color: [ACCENT],
  };
}

// Сл7: ГОБМП в разрезе соисполнителей
function chartCoExecutors2024(): any {
  const labels = [
    "Соисп.1",
    "Соисп.2",
    "Соисп.3",
    "Соисп.4",
    "Соисп.5",
    "Соисп.6",
    "Соисп.7",
    "Соисп.8",
  ];
  const values = [4200, 3800, 3600, 3300, 2900, 2500, 2100, 1800];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 120, right: 20, top: 10, bottom: 40 },
    xAxis: { type: "value", name: "кол-во" },
    yAxis: { type: "category", data: labels },
    series: [{ name: "Соисполнители", type: "bar", data: values }],
    color: [ACCENT],
  };
}

// Сл8: ГОБМП с ПО в 2024
function chartPO2024(): any {
  const labels = ["ПО1", "ПО2", "ПО3", "ПО4", "ПО5", "ПО6"];
  const values = [5200, 4100, 3800, 3000, 2600, 1900];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 100, right: 20, top: 10, bottom: 40 },
    xAxis: { type: "value", name: "кол-во" },
    yAxis: { type: "category", data: labels },
    series: [{ name: "ПО, ГОБМП 2024", type: "bar", data: values }],
    color: ["#6366f1"],
  };
}

// Сл9: ПО1 по ВА/ФАП
function chartPO1_VA_FAP_2024(): any {
  const labels = ["ВА1", "ВА2", "ВА3", "ФАП1", "ФАП2", "ФАП3"];
  const values = [2100, 1900, 1700, 1500, 1300, 900];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 80, right: 20, top: 10, bottom: 40 },
    xAxis: { type: "value", name: "кол-во" },
    yAxis: { type: "category", data: labels },
    series: [{ name: "ПО1, ГОБМП 2024", type: "bar", data: values }],
    color: [ACCENT],
  };
}

// Сл10: ПО1 по врачам (длинный список)
function chartPO1_Doctors_2024(): any {
  const doctors = [
    "Иванов И.И.",
    "Петров П.П.",
    "Сидорова А.А.",
    "Ким К.К.",
    "Ахметова Ж.Ж.",
    "Нурланов Д.Д.",
    "Сергеев В.В.",
    "Абдуллаев Р.Р.",
    "Тлеуова А.Т.",
    "Кузнецова Л.Л.",
    "Омаров Б.Б.",
    "Жумагалиев Е.Е.",
  ];
  const values = [980, 960, 920, 880, 840, 820, 780, 760, 740, 720, 700, 680];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 160, right: 20, top: 10, bottom: 40 },
    xAxis: { type: "value", name: "кол-во" },
    yAxis: { type: "category", data: doctors },
    dataZoom: [{ type: "slider", yAxisIndex: 0, right: 0, start: 0, end: 60 }],
    series: [{ name: "ПО1, врачи", type: "bar", data: values }],
    color: ["#6366f1"],
  };
}

// Сл11: ПО1 по МКБ-10
function chartPO1_MKB10_2024(): any {
  const mkb = ["I10", "E11", "J45", "M54", "N39", "K35", "G40", "I20"];
  const values = [2100, 1800, 1600, 1450, 1300, 1150, 900, 780];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 40, right: 20, top: 10, bottom: 80 },
    xAxis: { type: "category", data: mkb },
    yAxis: { type: "value", name: "кол-во" },
    series: [{ name: "ПО1, ГОБМП", type: "bar", data: values }],
    color: ["#f97316"],
  };
}

// Сл12: ГОБМП по месту оказания
function chartGOBMP_ByContext(): any {
  const data = [
    { name: "Амбулатория", value: 18000 },
    { name: "Стационар", value: 9500 },
    { name: "Скорая помощь", value: 3200 },
  ];
  return {
    tooltip: { trigger: "item" },
    legend: { orient: "vertical", left: "left" },
    series: [
      {
        name: "ГОБМП",
        type: "pie",
        radius: ["40%", "70%"],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 8, borderColor: "#fff", borderWidth: 2 },
        label: { show: false, position: "center" },
        emphasis: { label: { show: true, fontSize: 16, fontWeight: "bold" } },
        labelLine: { show: false },
        data,
      },
    ],
    color: [ACCENT, "#6366f1", "#22c55e"],
  };
}

// Сл13: ГОБМП 2024 по ВА1
function chartGOBMP2024_ByVA1(): any {
  const labels = ["Квартал 1", "Квартал 2", "Квартал 3", "Квартал 4"];
  const values = [3000, 3200, 3400, 3800];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 40, right: 20, top: 30, bottom: 40 },
    xAxis: { type: "category", data: labels },
    yAxis: { type: "value", name: "кол-во услуг" },
    series: [{ name: "ВА1, 2024", type: "bar", data: values }],
    color: [ACCENT],
  };
}

// Сл14: февраль 2024 по ВА1
function chartGOBMP_Feb2024_ByVA1(): any {
  const labels = ["Нед1", "Нед2", "Нед3", "Нед4"];
  const values = [820, 860, 790, 830];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 40, right: 20, top: 30, bottom: 40 },
    xAxis: { type: "category", data: labels },
    yAxis: { type: "value", name: "кол-во услуг" },
    series: [{ name: "Февраль 2024, ВА1", type: "bar", data: values }],
    color: ["#6366f1"],
  };
}

// Сл15: медработники по ВА1
function chartMedWorkers_ByVA1(): any {
  const workers = [
    "Иванов И.И.",
    "Петров П.П.",
    "Сидорова А.А.",
    "Ким К.К.",
    "Ахметова Ж.Ж.",
    "Нурланов Д.Д.",
    "Сергеев В.В.",
    "Абрамова И.И.",
    "Жумагалиев Е.Е.",
    "Семенова Т.Т.",
    "Омаров Б.Б.",
    "Тлеуова А.Т.",
  ];
  const values = [480, 450, 430, 410, 395, 380, 360, 340, 330, 320, 310, 300];
  return {
    tooltip: { trigger: "axis" },
    grid: { left: 180, right: 20, top: 10, bottom: 40 },
    xAxis: { type: "value", name: "кол-во" },
    yAxis: { type: "category", data: workers },
    dataZoom: [{ type: "slider", yAxisIndex: 0, right: 0, start: 0, end: 60 }],
    series: [{ name: "Медработники ВА1", type: "bar", data: values }],
    color: ["#22c55e"],
  };
}

/* ========= Конфиг слайдов ========= */

const chartsConfig: {
  id: SlideId;
  title: string;
  subtitle: string;
  option: () => any;
  tall?: boolean;
}[] = [
  {
    id: "sl2",
    title: "Количество услуг по ГОБМП",
    subtitle: "По месяцам 2024 года.",
    option: chartServicesGOBMP,
  },
  {
    id: "sl3",
    title: "Количество услуг по ОСМС",
    subtitle: "По месяцам 2024 года.",
    option: chartServicesOSMS,
  },
  {
    id: "sl4",
    title: "Количество услуг по ГОБМП и ОСМС по годам",
    subtitle: "Stacked bar: сравнение программ по годам.",
    option: chartGOBMP_OSMS_ByYears,
  },
  {
    id: "sl5",
    title: "Количество услуг по ГОБМП в 2024 году",
    subtitle: "Линейный график по месяцам. Клик → таблица.",
    option: chartGOBMP2024_ByMonth,
  },
  {
    id: "sl6",
    title: "Количество услуг по ГОБМП в феврале 2024 года",
    subtitle: "Столбчатый график по дням. Клик → таблица за февраль.",
    option: chartGOBMP_Feb2024_ByDay,
  },
  {
    id: "sl7",
    title: "Количество услуг по ГОБМП в разрезе соисполнителей в 2024 году",
    subtitle:
      "Горизонтальные столбцы, сортировка по убыванию. Клик → таблица по соисполнителям.",
    option: chartCoExecutors2024,
    tall: true,
  },
  {
    id: "sl8",
    title: "Количество услуг по ГОБМП с ПО в 2024 году",
    subtitle: "Горизонтальные столбцы по видам ПО. Клик → таблица с ПО.",
    option: chartPO2024,
  },
  {
    id: "sl9",
    title: "Количество услуг по ГОБМП с ПО1 по ВА/ФАП за 2024 год",
    subtitle: "Разрез по ВА/ФАП. Клик → переход к следующему уровню.",
    option: chartPO1_VA_FAP_2024,
  },
  {
    id: "sl10",
    title: "Количество услуг по ГОБМП с ПО1 по врачам за 2024 год",
    subtitle: "Длинный список врачей, вертикальная прокрутка. Клик → таблица по врачам.",
    option: chartPO1_Doctors_2024,
    tall: true,
  },
  {
    id: "sl11",
    title: "Количество услуг по ГОБМП с ПО1 по МКБ-10 за 2024 год",
    subtitle: "Разрез по кодам МКБ-10. Клик → таблица по МКБ-10.",
    option: chartPO1_MKB10_2024,
  },
  {
    id: "sl12",
    title: "Количество услуг по ГОБМП",
    subtitle: "Доля по месту оказания: амбулатория, стационар, скорая помощь.",
    option: chartGOBMP_ByContext,
  },
  {
    id: "sl13",
    title: "Количество услуг по ГОБМП в 2024 году по ВА1",
    subtitle: "По кварталам 2024. Клик → переход к февралю по ВА1.",
    option: chartGOBMP2024_ByVA1,
  },
  {
    id: "sl14",
    title: "Количество услуг по ГОБМП в феврале 2024 года по ВА1",
    subtitle: "По неделям февраля 2024. Клик → переход к медработникам по ВА1.",
    option: chartGOBMP_Feb2024_ByVA1,
  },
  {
    id: "sl15",
    title: "Количество услуг по медработникам по ВА1",
    subtitle: "Длинный список по медработникам. Клик → таблица по медработникам.",
    option: chartMedWorkers_ByVA1,
    tall: true,
  },
];

/* ========= Обработчик клика (заглушки для интерактива) ========= */

function useChartClickHandler() {
  return useCallback((slideId: SlideId, params: any) => {
    switch (slideId) {
      case "sl2":
        alert("Сл2 → в бою будет переход к Сл3 / детализация ГОБМП.");
        break;
      case "sl3":
        alert("Сл3 → в бою будет переход к Сл4 (ГОБМП/ОСМС по годам).");
        break;
      case "sl4":
        alert("Сл4 → в бою будет переход к Сл5 (ГОБМП 2024 по месяцам).");
        break;
      case "sl5":
        alert(
          "Сл5 → новая вкладка с таблицей: №, Наименование услуг, Кол-во, Сумма."
        );
        break;
      case "sl6":
        alert(
          "Сл6 → таблица за февраль: №, Наименование услуг, Кол-во, Сумма."
        );
        break;
      case "sl7":
        alert("Сл7 → таблица по структурным подразделениям/соисполнителям.");
        break;
      case "sl8":
        alert("Сл8 → таблица по ПО: №, ПО, Кол-во услуг, Сумма.");
        break;
      case "sl9":
        alert("Сл9 → переход к врачам/следующему уровню (Сл10/Сл16).");
        break;
      case "sl10":
        alert("Сл10 → таблица по врачам: №, ФИО, Кол-во, Сумма.");
        break;
      case "sl11":
        alert("Сл11 → таблица по МКБ-10: №, Код, Кол-во, Сумма.");
        break;
      case "sl12":
        alert("Сл12 → переход к ВА1 (Сл13).");
        break;
      case "sl13":
        alert("Сл13 → переход к февралю ВА1 (Сл14).");
        break;
      case "sl14":
        alert("Сл14 → переход к медработникам ВА1 (Сл15).");
        break;
      case "sl15":
        alert(
          "Сл15 → таблица по медработникам ВА1: №, ФИО, Кол-во, Сумма."
        );
        break;
      default:
        console.log("click", slideId, params);
    }
  }, []);
}

/* ========= Страница /center ========= */

export default function Center() {
  const onChartClick = useChartClickHandler();

  return (
    <div style={pageCss}>
      <div style={containerCss}>
        <header style={headerCss}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <div>
              <div style={titleCss}>
                Центр визуализации — прототип (слайды 2–15)
              </div>
              <div style={subtitleCss}>
                Единый лонгрид с демо-графиками по ГОБМП/ОСМС, ВА/ФАП, врачам и
                МКБ-10.
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Данные условные. В бою будут подгружаться по API.
            </div>
          </div>

          {/* Визуальный фильтр периода (пока без логики) */}
          <div style={filterBarCss}>
            <select style={filterInputCss as any} defaultValue="year">
              <option value="year">2024 год</option>
              <option value="month">Февраль 2024</option>
              <option value="range">Произвольный период</option>
            </select>
            <input type="date" style={filterInputCss as any} />
            <input type="date" style={filterInputCss as any} />
          </div>
        </header>

        <main style={sectionListCss}>
          {chartsConfig.map((cfg) => (
            <section key={cfg.id} style={cardCss}>
              <div style={cardTitleCss}>{cfg.title}</div>
              <div style={cardSubCss}>{cfg.subtitle}</div>
              <div style={cfg.tall ? tallChartCss : chartContainerCss}>
                <ReactECharts
                  option={cfg.option()}
                  style={{ width: "100%", height: "100%" }}
                  onEvents={{
                    click: (params: any) => onChartClick(cfg.id, params),
                  }}
                  notMerge={true}
                  lazyUpdate={true}
                />
              </div>
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}
